import React, { useState, useEffect } from 'react';
import {
    signInWithEmailAndPassword,
    createUserWithEmailAndPassword,
    signOut,
    onAuthStateChanged
} from 'firebase/auth';

const AuthContext = React.createContext();
const PROFILE_CACHE_KEY = 'vortx_user_profile';

// Load cached profile from sessionStorage
function getCachedProfile() {
    try {
        const cached = sessionStorage.getItem(PROFILE_CACHE_KEY);
        if (cached) {
            return JSON.parse(cached);
        }
    } catch (e) {
        console.warn('[AuthProvider] Failed to load cached profile:', e);
    }
    return null;
}

// Save profile to sessionStorage
function setCachedProfile(profile) {
    try {
        if (profile && !profile.noAccess) {
            sessionStorage.setItem(PROFILE_CACHE_KEY, JSON.stringify(profile));
        } else {
            sessionStorage.removeItem(PROFILE_CACHE_KEY);
        }
    } catch (e) {
        console.warn('[AuthProvider] Failed to cache profile:', e);
    }
}

/**
 * AuthProvider - Firebase authentication provider with Platform API integration
 *
 * Provides authentication state and methods to child components.
 * Integrates with Firebase Auth and Platform API for user profile management.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Child components
 * @param {string} props.apiBaseUrl - Platform API base URL (e.g., 'https://vortx-platform-api-202472576296.us-central1.run.app')
 * @param {Object} props.firebaseAuth - Firebase Auth instance from getAuth()
 * @param {string} [props.profileEndpoint='/api/site_dna/v1/profile'] - Profile API endpoint path
 *
 * @example
 * // In your app's root:
 * import { initializeApp } from 'firebase/app';
 * import { getAuth } from 'firebase/auth';
 * import { AuthProvider } from '@vortx/shared-ui';
 *
 * const app = initializeApp(firebaseConfig);
 * const auth = getAuth(app);
 *
 * function App() {
 *   return (
 *     <AuthProvider
 *       apiBaseUrl="https://vortx-platform-api-202472576296.us-central1.run.app"
 *       firebaseAuth={auth}
 *     >
 *       <YourApp />
 *     </AuthProvider>
 *   );
 * }
 */
export function AuthProvider({
    children,
    apiBaseUrl,
    firebaseAuth,
    profileEndpoint = '/api/site_dna/v1/profile'
}) {
    const [currentUser, setCurrentUser] = useState(null);
    const [userProfile, setUserProfile] = useState(getCachedProfile);
    const [profileError, setProfileError] = useState(null);
    const [loading, setLoading] = useState(true);

    if (!apiBaseUrl) {
        throw new Error('AuthProvider requires apiBaseUrl prop');
    }

    if (!firebaseAuth) {
        throw new Error('AuthProvider requires firebaseAuth prop');
    }

    function signup(email, password) {
        return createUserWithEmailAndPassword(firebaseAuth, email, password);
    }

    function login(email, password) {
        return signInWithEmailAndPassword(firebaseAuth, email, password);
    }

    function logout() {
        setUserProfile(null);
        setCachedProfile(null);
        return signOut(firebaseAuth);
    }

    // Fetch user profile from backend
    async function fetchUserProfile(user) {
        if (!user) {
            setUserProfile(null);
            setCachedProfile(null);
            return;
        }

        // Use cached profile while fetching fresh data
        const cached = getCachedProfile();
        if (cached && cached.email === user.email) {
            setUserProfile(cached);
        }

        try {
            const token = await user.getIdToken();
            const res = await fetch(`${apiBaseUrl}${profileEndpoint}`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            // Only set noAccess if explicitly returned 403 with NO_PRODUCT_ACCESS
            if (res.status === 403) {
                const data = await res.json();
                if (data.code === 'NO_PRODUCT_ACCESS') {
                    const noAccessProfile = { noAccess: true, email: user.email };
                    setUserProfile(noAccessProfile);
                    setCachedProfile(null);
                    return;
                }
            }

            if (!res.ok) {
                console.error('[AuthProvider] Profile fetch failed with status:', res.status);
                setProfileError(`Failed to load user profile (HTTP ${res.status}). User permissions and app access cannot be determined.`);
                if (!cached) {
                    setUserProfile(null);
                }
                return;
            }

            const profile = await res.json();
            setProfileError(null);

            // If Accounts Service was unavailable (cold start), the profile
            // won't have admin flags. Keep cached profile if we have one,
            // and retry in 3s when the service should be warm.
            if (profile.auth_context_available === false) {
                console.warn('[AuthProvider] Auth context unavailable (Accounts Service cold start) — retrying in 3s');
                const cached = getCachedProfile();
                if (cached && cached.email === user.email) {
                    // Keep showing cached profile (with admin flags) while we retry
                } else {
                    // No cache — show partial profile for now
                    setUserProfile(profile);
                }
                setTimeout(() => fetchUserProfile(user), 3000);
                return;
            }

            setUserProfile(profile);
            setCachedProfile(profile);
        } catch (err) {
            console.error('[AuthProvider] Error fetching user profile:', err);
            setProfileError(`Failed to load user profile: ${err.message}. User permissions and app access cannot be determined.`);
            if (!cached) {
                setUserProfile(null);
            }
        }
    }

    useEffect(() => {
        let authResolved = false;
        let timeoutId = null;

        const unsubscribe = onAuthStateChanged(firebaseAuth, async (user) => {
            console.warn('[DEBUG] onAuthStateChanged fired:', {
                hasUser: !!user,
                email: user?.email,
                currentPath: window.location.pathname,
                authResolved
            });

            setCurrentUser(user);

            // Firebase race condition fix:
            // onAuthStateChanged can fire with null before session is restored.
            // If we have a cached profile (evidence of prior login), don't immediately
            // set loading=false on null - wait for potential second auth event.
            const cachedProfile = getCachedProfile();

            if (user) {
                // Wait for profile (including admin flags) before showing the app.
                // The cached profile provides an optimistic preview via fetchUserProfile,
                // but we need the full fetch to complete so admin privileges are resolved.
                await fetchUserProfile(user);
                authResolved = true;
                if (timeoutId) clearTimeout(timeoutId);
                setLoading(false);
            } else if (!cachedProfile) {
                // No cached profile means user was never logged in - safe to redirect to login
                fetchUserProfile(user); // clears profile state
                setLoading(false);
            } else {
                // Got null but have cached profile - wait briefly for session restore
                // This handles the Firebase race condition where:
                // 1. null fires before session restore on initial load
                // 2. IndexedDB storage event from another tab causes temporary null
                // In both cases, if we have evidence of prior login (cached profile),
                // wait for Firebase to restore the session before redirecting
                console.warn('[DEBUG] AuthProvider: got null but have cached profile, waiting for session restore...');
                if (timeoutId) clearTimeout(timeoutId);
                timeoutId = setTimeout(() => {
                    console.warn('[DEBUG] AuthProvider: session restore timeout, setting loading=false');
                    authResolved = true;
                    fetchUserProfile(user); // clears profile state
                    setLoading(false);
                }, 1500); // Give Firebase 1.5s to restore session
            }
        });

        return () => {
            unsubscribe();
            if (timeoutId) clearTimeout(timeoutId);
        };
    }, [firebaseAuth, apiBaseUrl, profileEndpoint]);

    // Refresh user profile (call after profile updates)
    async function refreshUserProfile() {
        if (currentUser) {
            await fetchUserProfile(currentUser);
        }
    }

    const value = {
        currentUser,
        userProfile,
        profileError,
        loading,
        login,
        signup,
        logout,
        refreshUserProfile
    };

    return (
        <AuthContext.Provider value={value}>
            {!loading && children}
        </AuthContext.Provider>
    );
}

export { AuthContext };
